import React, { useState } from "react";
import i18n from "i18next";
import { Button, Input, Select, Spin, Table } from "antd";
import { CheckOutlined, CopyOutlined, KeyOutlined, ReloadOutlined } from "@ant-design/icons";
import { formatAppDateTime } from "util/formatAppDate";
import {
  BrandTag,
  Notice,
  QueueStatus,
  TRIGGER_KEY,
  crmSampleRequest,
  resultText,
} from "pages/SurveyManagement/phoneTriggerShared";
import PlanEndedNotice from "components/General/PlanEndedNotice";
import styles from "./style.module.scss";

const K = "crmConnection";

const when = (value) => (value ? formatAppDateTime(value, i18n.language, { useDayjs: false }) : "");

const Step = ({ t, number, done, title, desc, children }) => (
  <div className={styles.step}>
    <div className={`${styles.stepIcon} ${done ? styles.stepIconDone : ""}`}>{done ? <CheckOutlined /> : number}</div>
    <div className={styles.stepBody}>
      <div className={styles.stepTop}>
        <h4>{title}</h4>
        <span className={done ? styles.badge : styles.badgePending}>{t(done ? `${K}.done` : `${K}.pending`)}</span>
      </div>
      <p className={styles.stepDesc}>{desc}</p>
      {children}
    </div>
  </div>
);

const CopyField = ({ value, onCopy }) => (
  <div className={styles.copyRow}>
    <Input readOnly value={value} onFocus={(e) => e.target.select()} />
    <Button icon={<CopyOutlined />} onClick={() => onCopy(value)} />
  </div>
);

const KeyStep = ({ t, info, busy, newKey, onCreate, onRevoke, onCopy }) => (
  <Step t={t} number={1} done={!!info.key} title={t(`${K}.step_key_title`)} desc={t(`${K}.step_key_desc`)}>
    {newKey ? (
      <Notice title={t(`${K}.key_once_title`)}>
        <p className={styles.small}>{t(`${K}.key_once`)}</p>
        <CopyField value={newKey} onCopy={onCopy} />
      </Notice>
    ) : null}
    {info.key ? (
      <div className={styles.keyBox}>
        <div>
          <div className={styles.keyHint}>pvk_••••{info.key.hint}</div>
          <div className={styles.meta}>
            {t(`${K}.key_created`, { date: when(info.key.created_at) })}
            {" · "}
            {info.key.last_used_at ? t(`${K}.key_last_used`, { date: when(info.key.last_used_at) }) : t(`${K}.key_never_used`)}
          </div>
        </div>
        {info.read_only ? null : (
          <div className={styles.row}>
            <Button size="small" icon={<KeyOutlined />} loading={busy} onClick={onCreate}>
              {t(`${K}.rotate`)}
            </Button>
            <Button size="small" danger onClick={onRevoke}>
              {t(`${K}.revoke`)}
            </Button>
          </div>
        )}
      </div>
    ) : info.read_only ? null : (
      <Button type="primary" className={styles.cta} icon={<KeyOutlined />} loading={busy} onClick={onCreate}>
        {t(`${K}.create`)}
      </Button>
    )}
  </Step>
);

const WebhookStep = ({ t, info, url, surveyId, onSurveyChange, onCopy }) => {
  const [showSample, setShowSample] = useState(false);
  const surveys = info.surveys || [];
  const sample = crmSampleRequest(url, surveyId ?? 0);
  return (
    <Step
      t={t}
      number={2}
      done={!!info.key?.last_used_at}
      title={t(`${K}.step_url_title`)}
      desc={t(`${K}.step_url_desc`)}
    >
      <CopyField value={url} onCopy={onCopy} />
      <Button type="link" className={styles.sampleToggle} onClick={() => setShowSample((v) => !v)}>
        {t(showSample ? `${K}.hide_sample` : `${K}.show_sample`)}
      </Button>
      {showSample ? (
        <div className={styles.sample}>
          <div className={styles.sampleBar}>
            {surveys.length ? (
              <Select
                className={styles.surveySelect}
                value={surveyId}
                onChange={onSurveyChange}
                options={surveys.map((s) => ({ value: s.id, label: `${s.id} · ${s.title}` }))}
              />
            ) : (
              <span />
            )}
            <Button size="small" icon={<CopyOutlined />} onClick={() => onCopy(sample)}>
              {t(`${K}.copy`)}
            </Button>
          </div>
          <pre className={styles.code}>{sample}</pre>
          <p className={styles.small}>{t(`${K}.sample_hint`)}</p>
          <p className={styles.small}>{t(`${K}.responses`)}</p>
        </div>
      ) : null}
    </Step>
  );
};

const SurveyStep = ({ t, info }) => {
  const surveys = info.surveys || [];
  return (
    <Step
      t={t}
      number={3}
      done={surveys.some((s) => s.trigger_on)}
      title={t(`${K}.step_survey_title`)}
      desc={t(`${K}.step_survey_desc`)}
    >
      {surveys.length ? (
        <div className={styles.surveyList}>
          {surveys.map((s) => (
            <div key={s.id} className={styles.surveyRow}>
              <span className={styles.surveyId} title="survey_id">
                {s.id}
              </span>
              <div className={styles.surveyText}>
                <span>{s.title}</span>
                <span className={styles.meta}>{s.team}</span>
              </div>
              <BrandTag tone={s.trigger_on ? "purple" : "neutral"}>{t(s.trigger_on ? `${K}.calls_on` : `${K}.calls_off`)}</BrandTag>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>{t(`${K}.surveys_empty`)}</p>
      )}
    </Step>
  );
};

const SOURCE_KEYS = { call: "dnc_source_call", manual: "dnc_source_manual" };

// Numbers no survey calls any more: guests who asked during a call, and numbers added here.
const DoNotCallCard = ({ t, info, numbers, busy, onAdd, onRemove }) => {
  const [phone, setPhone] = useState("");
  const add = async () => {
    if (phone.trim() && (await onAdd(phone.trim()))) setPhone("");
  };
  const columns = [
    { title: t(`${K}.dnc_col_phone`), dataIndex: "phone", key: "phone" },
    {
      title: t(`${K}.dnc_col_source`),
      key: "source",
      render: (_, row) => t(`${K}.${SOURCE_KEYS[row.source] || "dnc_source_other"}`),
    },
    { title: t(`${K}.dnc_col_since`), key: "since", render: (_, row) => when(row.since) },
    ...(info.read_only
      ? []
      : [
          {
            title: "",
            key: "remove",
            align: "right",
            render: (_, row) => (
              <Button type="link" className={styles.sampleToggle} onClick={() => onRemove(row)}>
                {t(`${K}.dnc_remove`)}
              </Button>
            ),
          },
        ]),
  ];
  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h3>{t(`${K}.dnc_title`)}</h3>
      </div>
      <p className={styles.stepDesc}>{t(`${K}.dnc_desc`)}</p>
      {info.read_only ? null : (
        <div className={`${styles.copyRow} ${styles.dncAdd}`}>
          <Input
            value={phone}
            placeholder={t(`${K}.dnc_add_ph`)}
            onChange={(e) => setPhone(e.target.value)}
            onPressEnter={add}
          />
          <Button className={styles.cta} loading={busy} onClick={add} disabled={!phone.trim()}>
            {t(`${K}.dnc_add`)}
          </Button>
        </div>
      )}
      <Table
        size="small"
        rowKey="ref"
        columns={columns}
        dataSource={numbers || []}
        loading={numbers === null}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: t(`${K}.dnc_empty`) }}
      />
    </section>
  );
};

const Representation = ({
  t,
  info,
  loading,
  busy,
  newKey,
  url,
  surveyId,
  onSurveyChange,
  onBack,
  onCreate,
  onRevoke,
  onReload,
  onCopy,
  dnc = null,
  dncBusy = false,
  onAddDnc,
  onRemoveDnc,
}) => {
  const titles = Object.fromEntries((info?.surveys || []).map((s) => [s.id, s.title]));
  const recentColumns = [
    { title: t(`${K}.col_received`), key: "received", render: (_, row) => when(row.created_at) },
    { title: t(`${K}.col_survey`), key: "survey", render: (_, row) => titles[row.instrument_id] || row.instrument_id },
    { title: t(`${TRIGGER_KEY}.col_phone`), dataIndex: "phone", key: "phone" },
    { title: t(`${TRIGGER_KEY}.col_status`), key: "status", render: (_, row) => <QueueStatus t={t} row={row} /> },
    { title: t(`${TRIGGER_KEY}.col_result`), key: "result", render: (_, row) => resultText(t, row) },
  ];

  let body;
  if (loading && !info) {
    body = <Spin />;
  } else if (!info || info.error) {
    body = <Notice error>{t(info?.notReady ? `${K}.not_ready` : `${K}.error`)}</Notice>;
  } else if (!info.can_manage) {
    body = <Notice>{t(`${K}.members_only`)}</Notice>;
  } else {
    body = (
      <>
        {info.read_only ? <PlanEndedNotice product="survey" /> : null}
        {!info.read_only && info.calling && !info.calling.ready ? (
          <Notice>{t(`${TRIGGER_KEY}.dry_run_notice`)}</Notice>
        ) : null}

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>{t(`${K}.steps_title`)}</h3>
          </div>
          <KeyStep t={t} info={info} busy={busy} newKey={newKey} onCreate={onCreate} onRevoke={onRevoke} onCopy={onCopy} />
          <WebhookStep t={t} info={info} url={url} surveyId={surveyId} onSurveyChange={onSurveyChange} onCopy={onCopy} />
          <SurveyStep t={t} info={info} />
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>{t(`${K}.recent_title`)}</h3>
            <Button size="small" icon={<ReloadOutlined />} onClick={onReload} />
          </div>
          <Table
            size="small"
            rowKey="id"
            columns={recentColumns}
            dataSource={info.recent || []}
            pagination={false}
            scroll={{ x: true }}
            locale={{ emptyText: t(`${K}.recent_empty`) }}
          />
        </section>

        <DoNotCallCard t={t} info={info} numbers={dnc} busy={dncBusy} onAdd={onAddDnc} onRemove={onRemoveDnc} />
      </>
    );
  }

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={onBack}>
        ← {t("settings.334")}
      </button>
      <h2 className={styles.title}>{t(`${K}.title`)}</h2>
      <p className={styles.subtitle}>{t(`${K}.subtitle`)}</p>
      {body}
    </div>
  );
};

export default Representation;
